import b_ from 'b_';
import { Button, LessonPassportButton, Link, useAppContext, useGeneratePath } from 'platform-components/common';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router';
import ym from 'react-yandex-metrika';

import { useGetLessonRunUrl } from 'common.components/hooks';
import lessonActions from 'common.components/lesson/actions';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { SPEC_COURSE_LESSON_MODE } from 'common.components/spec-course-lesson-view/typings';
import { teamBuilderActions } from 'common.components/team-builder/actions';
import { TEAM_BUILDER_ENTRY_STEPS } from 'common.components/team-builder/typings';
import { useRegistrationLinkMetrika } from 'common.components/user/hooks';
import { useTeamRegistrationLink } from 'common.components/user/hooks/useTeamRegistrationLink';
import { YM_LOCATION } from 'utils/ym-constants';

import type { TeacherButtonPrepLesson } from '@yandex-int/k-common';
import { BUTTON_VIEW } from 'platform-components/src/components/button/types';
import { SpecCourseLessonViewRegistrationLinkControls } from '../__registration-link-controls/lesson-view__registration-link-controls';
import { MODES_TO_ADD_STUDENTS, MODES_OLYMP_IN_PROGRESS, MODES_TO_SHOW_TASKS } from '../../spec-course.store/constants';

import './lesson-view__passport-button.scss';

interface Props {
    mode: SPEC_COURSE_LESSON_MODE;
    buttonPrepLessonProps?: TeacherButtonPrepLesson;
}

const b = b_.with('teacher-spec-course-lesson-view-passport-button');

// eslint-disable-next-line complexity
export const SpecCourseLessonViewPassportButton = React.memo(({ mode, buttonPrepLessonProps }: Props) => {
    const { courseId, clessonId, classId } = useParams<{ courseId: string; clessonId: string; classId: string }>();
    const { search } = useLocation();
    const dispatch = useDispatch();
    const intl = useIntl();

    const olympSlug = useSelector(lessonSelectors.getLessonSlug);

    const { basePath, prefix, staticUrls } = useAppContext();
    const getRouterPath = useGeneratePath();

    const teamRegistrationLink = useTeamRegistrationLink();

    const getLessonRunUrl = useGetLessonRunUrl();

    const { ymTeacherAddSchool } = useRegistrationLinkMetrika();

    const lessonUrl = getLessonRunUrl({ clessonId: Number(clessonId), courseId: Number(courseId) });

    const handleAddStudents = useCallback(() => {
        dispatch(
            teamBuilderActions.start({
                entryPoint: TEAM_BUILDER_ENTRY_STEPS.STUDENTS,
                onSuccess: (addedStudentsIds?: number[]) => {
                    dispatch(
                        lessonActions.finishAddingStudentsRequest({
                            studentsIds: addedStudentsIds || null,
                            ymLocation: YM_LOCATION.OLYMP,
                        })
                    );
                    dispatch(
                        lessonActions.getClessonResultsRequest({
                            cLessonId: Number(clessonId),
                            courseId: Number(courseId),
                        })
                    );
                },
            })
        );
    }, [clessonId, courseId, dispatch]);

    const handleShowTasks = useCallback(() => {
        ym('reachGoal', 'teacher_olymp_see_as_student', { cl_id: clessonId });

        window.open(lessonUrl, '_blank', 'noopener');
    }, [clessonId, lessonUrl]);

    const handleAddSchool = () => {
        ymTeacherAddSchool('olympiad');
        dispatch(teamBuilderActions.start({ entryPoint: TEAM_BUILDER_ENTRY_STEPS.SCHOOL }));
    };

    const handleFallbackAddStudents = () => {
        dispatch(teamBuilderActions.start({ entryPoint: TEAM_BUILDER_ENTRY_STEPS.FALLBACK_STUDENTS }));
    };

    const buttonTextId = `specCourse.LessonView.PassportTip.${mode}.btn`;

    if (MODES_TO_ADD_STUDENTS.includes(mode)) {
        return (
            <LessonPassportButton
                mix="qa_spec-course-button_status-add_students"
                onClick={handleAddStudents}
                textId={buttonTextId}
                view={Button.VIEW.ACTION}
            />
        );
    } else if (MODES_TO_SHOW_TASKS.includes(mode)) {
        return (
            <LessonPassportButton
                mix="qa_spec-course-button_status-show_tasks"
                onClick={handleShowTasks}
                textId={buttonTextId}
                view={Button.VIEW.SECONDARY}
            />
        );
    } else if (MODES_OLYMP_IN_PROGRESS.includes(mode)) {
        const linkToShareOlymp = intl
            .formatMessage({
                id: `specCourse.LessonView.buttons.shareOlymp.link.${olympSlug}`,
                defaultMessage: ' ',
            })
            .trim();
        const routerPath = getRouterPath(staticUrls.labClassPrint, { classId });
        const linkToPrintLogins = `${basePath}${prefix}${routerPath}${search}`;
        const linkToPreparedLesson = intl.formatMessage({
            id: `specCourse.LessonView.buttons.showExamples.link.${olympSlug}`,
            defaultMessage: 'no-link',
        });

        return (
            <div className={b('multiply-btns')}>
                {buttonPrepLessonProps && (
                    <Link rel="noopener noreferrer" target="_blank" to={buttonPrepLessonProps.url}>
                        <LessonPassportButton
                            mix="qa_spec-course-button_status-show-prepared-lesson"
                            onClick={() => ym('reachGoal', buttonPrepLessonProps.metrikaGoal)}
                            textId={buttonPrepLessonProps.buttonText || ''}
                            view={(buttonPrepLessonProps.view as BUTTON_VIEW) || Button.VIEW.SECONDARY}
                        />
                    </Link>
                )}
                {linkToShareOlymp && (
                    <Link rel="noopener noreferrer" target="_blank" to={linkToShareOlymp}>
                        <LessonPassportButton
                            mix="qa_spec-course-button_status-share-with-olymp"
                            textId="specCourse.LessonView.buttons.shareOlymp"
                        />
                    </Link>
                )}
                <Link rel="noopener noreferrer" target="_blank" to={linkToPrintLogins}>
                    <LessonPassportButton
                        mix="qa_spec-course-button_status-print-student-logins"
                        textId="specCourse.LessonView.buttons.printLogins"
                        view={Button.VIEW.SECONDARY}
                    />
                </Link>
                {linkToPreparedLesson !== 'no-link' && !buttonPrepLessonProps && (
                    <Link rel="noopener noreferrer" target="_blank" to={linkToPreparedLesson}>
                        <LessonPassportButton
                            textId="specCourse.LessonView.buttons.showExamples"
                            view={Button.VIEW.SECONDARY}
                        />
                    </Link>
                )}
            </div>
        );
    } else if (mode === SPEC_COURSE_LESSON_MODE.NO_SCHOOL) {
        return (
            <LessonPassportButton
                mix="qa_spec-course-button_status-add_school"
                onClick={handleAddSchool}
                textId={buttonTextId}
                view={Button.VIEW.ACTION}
            />
        );
    } else if (mode === SPEC_COURSE_LESSON_MODE.INVITE_STUDENTS) {
        return (
            <SpecCourseLessonViewRegistrationLinkControls
                link={teamRegistrationLink}
                onFallbackClick={handleFallbackAddStudents}
            />
        );
    }

    return null;
});
